<template>
    <div :class="'h-screen w-full flex flex-col'">
        <Anouncement
    		:key="announcements.length"
    		v-if="announcements.length && isAuthenticated()"
            class="h-[50px]"
            @closed="handleCloseAnnouncement"
            :announcements="announcements"

        />

        <BeachCommuteWai
            v-if="isBeachComuteUser()"
        />

        <div
    		v-if="loaded"
    		:class="[
                isBeachComuteUser() && announcements.length > 0 ?
                'h-[calc(100vh-118px)]': isBeachComuteUser() ?
                'h-[calc(100vh-68px)]' : announcements.length > 0 ?
                'h-[calc(100vh-50px)]' : 'h-full',
                'flex',
            ]"
    	>

    		<sidebar
    			@new-resource="handleNewResource"
    		/>
    
    		<div class="w-full flex flex-col">
            <AppHeader/>

            <div v-if="isMovedToNextStage" class="fireworks-container fixed top-0 left-0 w-full h-full z-[99999]"></div>
    			<div
    				class="w-full h-full relative grow min-h-0 overflow-auto flex"
    			>
                    <div class="grow">
                      <slot />
                    </div>

                    <SidebarJobSearchPlan v-if="isJobSearchBlockersEnabled() && isAuthenticated()"/>
    			</div>
    		</div>
        </div>
    
        <AppointmentCalendly
          @close="handleCloseCalendlyAppointment"
          v-if="calendlyAppointmentVisible && isAuthenticated()"
        />
    
        <WebinarModal
          :showModal="showWebinarModal"
          :closeModal="hideWebinarModal && isAuthenticated()"
        />
    
        <ChangeTemplateModal
          v-if="!isCoverLetter && loaded"
          :templates="templates"
          :visible="selectTemplateVisible"
          @close="() => selectTemplateVisible=false"
          @change="handleChangeTemplate"
        />
        
        <ExistingUserSubscriptionModal v-if="isAuthenticated"/>
        <LimitExceededModal v-if="isAuthenticated()"/>
    </div>
</template>

<script setup>
import { storeToRefs } from 'pinia'

import { hashInput, isJobSearchBlockersEnabled } from '../utils/helpers'

import { computed, onMounted, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { setAuthState } from '../composables/auth'
import { useBuilder } from '../store/builder.store'
import { useFeatureUsageStore } from '../store/feature-usage.store'
import { useSidebarStore } from '../store/sidebar.store'
import { useOnboardingChecklist } from '../store/onboarding-checklist.store'
import { useJobSearchPlanStore } from '../store/job-search-plan.store'

import { getAnnouncements } from '../api/announcements'
import { getAll as getAllTemplates } from '../api/templates'

// import components
import Sidebar from '../components/sidebar'
import AppHeader from "../components/app-header.vue"
import Anouncement from '../components/anouncement'
import BeachCommuteWai from '../components/beach-commute-wai.vue'

import AppointmentCalendly from '../modals/appointment-calendly'
import ChangeTemplateModal from "../modals/change-template";
import WebinarModal from "../modals/webinar-modal.vue"

import ExistingUserSubscriptionModal from "../modals/subscription/existing-user-subscription-modal.vue";
import LimitExceededModal from "../modals/limit-exceeded.vue";
import SidebarJobSearchPlan from "../components/job-search-plan/sidebar-job-search-plan.vue";

const router = useRouter()
const route = useRoute()

const isCoverLetter = ref(false)
const showWebinarModal = ref(false)

watch(
  () => route.meta?.coverLetter,
  async newIsCoverLetter => {
    isCoverLetter.value = newIsCoverLetter ?? false
  }, { immediate: true }
)

const { isAuthenticated, getUser, isBeachComuteUser, setUser } = setAuthState()

const builder = useBuilder()
const featuresUsageStore = useFeatureUsageStore();
const sidebarStore = useSidebarStore();
const onboardingChecklistStore = useOnboardingChecklist();
const jobSearchPlanStore = useJobSearchPlanStore();
const { collapsed } = storeToRefs(sidebarStore)

const calendlyAppointmentVisible = ref(false);

const { isMovedToNextStage } = storeToRefs(jobSearchPlanStore);

onMounted(()=> {
  onboardingChecklistStore.handleCloseOnboardingChecklist()
  
  if(localStorage.getItem(`showWebinarPopup${hashInput(getUser()?.email ?? '')}`) == 'true'){
    showWebinarModal.value = true;
  }

  if(localStorage.getItem(`newCalendlyAppointmentClosed${hashInput(getUser()?.email ?? '')}`) == 'true'){
    calendlyAppointmentVisible.value = true;
  }
  
  if(!getUser()?.options?.onboarding_checklist_passed && getUser()?.options?.home_passed) {
      onboardingChecklistStore.handleShowOnboardingChecklist()
  }
})

const hideWebinarModal = () => {
  showWebinarModal.value = false;
  localStorage.removeItem(`showWebinarPopup${hashInput(getUser()?.email ?? '')}`)
}

const handleCloseCalendlyAppointment = () => {
  calendlyAppointmentVisible.value = false;
  localStorage.removeItem(`newCalendlyAppointmentClosed${hashInput(getUser()?.email ?? '')}`);
}

const announcements = ref([])
const closedAnnouncements = localStorage.getItem('closedAnnouncements') 
    ? JSON.parse(localStorage.getItem('closedAnnouncements'))
    : []

const handleCloseAnnouncement = (uuid) => {
  announcements.value = announcements.value.filter(el => uuid != el.uuid)
}

const loaded = ref(false)
const selectTemplateVisible = ref(false)

const handleNewResource = async() => {
   // allow to change templates only for resumes
  if(! isCoverLetter.value) {
    if(templates.value.length) {
      selectTemplateVisible.value = true
      return
    }
  }

  try {
    await builder.init(null, router.replace, route)
  }catch (err) {
    if(err?.response?.status === 429) {
      featuresUsageStore.setShowLimitExceed('cover_letter')
    }
  }
}

const templates = ref([])

const handleChangeTemplate = ({ template }) => {
  localStorage.setItem('defaultTemplate', template)
  builder.init(null, router.replace, route)
}

onMounted(async() => {
  if(isAuthenticated())  {
		try {
			let [announcementsResults, templatesResults] = await Promise.all([getAnnouncements(), getAllTemplates()])
			announcements.value = announcementsResults.filter(el => !closedAnnouncements.includes(el.uuid))
			templates.value = templatesResults
		}catch (e) {
			//ignore
		}finally {
			loaded.value = true
		}
  }else{
	  loaded.value = true
  }

  collapsed.value = sidebarStore.resolveIfCollapsed(route.name)

  window.addEventListener("resize", () => {
    collapsed.value = sidebarStore.resolveIfCollapsed(route.name)
  });
})
</script>
